import React, { useState, useEffect } from 'react';
import { Sheet } from 'react-modal-sheet';
import { Player } from 'video-react';
import Btn from '../Btn/Btn';
import './firstTimeModal.css';
import { useTranslation } from 'react-i18next';

const FirstTimeModal = ({ 
  isOpen, 
  onClose, 
  routeType, 
  videoUrl, 
  titleKey,
  descriptionKey,
  forceShow = false
}) => {
  const { t } = useTranslation('basic');
  const [hasSeen, setHasSeen] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const modalTitle = titleKey ? t(titleKey) : t('instruction');
  const modalDescription = descriptionKey ? t(descriptionKey) : t('watch_short_instruction');

  useEffect(() => {
    if (isOpen && routeType) {
      const storageKey = `firstTime_${routeType}`;
      const hasSeenBefore = localStorage.getItem(storageKey);
      
      // If forceShow is true, always show the modal regardless of localStorage
      if (forceShow || !hasSeenBefore) {
        setShowModal(true);
        setHasSeen(false);
      } else {
        setHasSeen(true);
        setShowModal(false);
      }
    } else {
      setShowModal(false);
    }
  }, [isOpen, routeType, forceShow]);

  const handleClose = () => {
    setShowModal(false);
    setHasSeen(true);
    onClose();
  };

  const handleSkip = () => {
    handleClose();
  };

  const handleDontShowAgain = () => {
    if (routeType) {
      const storageKey = `firstTime_${routeType}`;
      localStorage.setItem(storageKey, 'true');
    }
    setShowModal(false);
    setHasSeen(true);
    onClose();
  };

  // Show modal if forceShow is true OR if user hasn't seen it before
  if (!showModal || (hasSeen && !forceShow)) {
    return null;
  }

  return (
    <Sheet isOpen={showModal} onClose={handleClose}>
      <Sheet.Backdrop onClick={handleClose} />
      <Sheet.Container className="first-time-modal-container">
        <Sheet.Content>
          <div className="first-time-modal-wrapper">
            {/* Fixed Header */}
            <div className="modal-header">
              <div className="modal-title-section">
                <div className="modal-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="#17C653"/>
                  </svg>
                </div>
                <h2 className="modal-title">{modalTitle}</h2>
              </div>
              <button className="close-button" onClick={handleClose} id="first-time-modal-close-btn">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M18 18L6 6" stroke="#C4CADA" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="modal-scrollable-content">
              {/* Description */}
              <div className="modal-description">
                <p>{modalDescription}</p>
              </div>

              {/* Video Player */}
              <div className="video-container">
                {videoUrl ? (
                  <Player
                    autoPlay
                    preload="metadata"
                    src={videoUrl}
                    className="video-player"
                    fluid={true}
                    aspectRatio="16:9"
                  />
                ) : (
                  <div className="video-placeholder">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M24 4C12.95 4 4 12.95 4 24C4 35.05 12.95 44 24 44C35.05 44 44 35.05 44 24C44 12.95 35.05 4 24 4ZM20 32V16L32 24L20 32Z" fill="#C4CADA"/>
                    </svg>
                    <p>{t('video_instruction')}</p>
                  </div>
                )}
              </div>

              {/* Full screen link */}
              {videoUrl && (
                <div className="fullscreen-link">
                  <p>{t('not_opening_fullscreen')}</p>
                  <button 
                    className="link-button"
                    id="first-time-modal-fullscreen-btn"
                    onClick={() => {
                      // Open video directly in new window
                      const newWindow = window.open('', '_blank');
                      if (newWindow) {
                        newWindow.document.write(`
                          <!DOCTYPE html>
                          <html>
                          <head>
                            <title>${t('video_instruction')}</title>
                            <style>
                              body {
                                margin: 0;
                                padding: 20px;
                                background: #000;
                                display: flex;
                                justify-content: center;
                                align-items: center;
                                min-height: 100vh;
                                font-family: Arial, sans-serif;
                              }
                              .video-container {
                                max-width: 100%;
                                max-height: 100vh;
                              }
                              video {
                                width: 100%;
                                height: auto;
                                max-height: 90vh;
                                border-radius: 8px;
                              }
                              .close-button {
                                position: fixed;
                                top: 20px;
                                right: 20px;
                                background: rgba(255, 255, 255, 0.2);
                                border: none;
                                color: white;
                                padding: 10px 15px;
                                border-radius: 5px;
                                cursor: pointer;
                                font-size: 14px;
                              }
                              .close-button:hover {
                                background: rgba(255, 255, 255, 0.3);
                              }
                            </style>
                          </head>
                          <body>
                            <button class="close-button" onclick="window.close()">${t('close')}</button>
                            <div class="video-container">
                              <video controls autoplay>
                                <source src="${videoUrl}" type="video/mp4">
                                ${t('browser_not_support_video')}
                              </video>
                            </div>
                          </body>
                          </html>
                        `);
                        newWindow.document.close();
                      } else {
                        // Fallback: open video URL directly
                        window.open(videoUrl, '_blank');
                      }
                    }}
                  >
                    {t('open_in_new_tab')}
                  </button>
                </div>
              )}
            </div>

            {/* Fixed Action buttons */}
            <div className="modal-actions">
              <Btn 
                onClick={handleDontShowAgain} 
                text={t('understood')} 
                width="100%"
                className="primary-button"
                id="first-time-modal-understand-btn"
              />
              <button 
                className="skip-button"
                onClick={handleSkip}
                id="first-time-modal-skip-btn"
              >
                {t('skip')}
              </button>
              <button 
                className="dont-show-button"
                onClick={handleDontShowAgain}
                id="first-time-modal-dont-show-btn"
              >
                {t('dont_show_again')}
              </button>
            </div>
          </div>
        </Sheet.Content>
      </Sheet.Container>
    </Sheet>
  );
};

export default FirstTimeModal; 